// src/pages/ProductImageGenPage.js
import React, { useState, useRef, useEffect } from 'react';
import { Upload, Download, Loader2, X, Image as ImageIcon, LayoutTemplate, Camera, Settings, Wand2, History, Trash2, Sparkles, Layers, Package, BookMarked, Search, Save, Tag as TagIcon } from 'lucide-react';
import GeminiApiKeyInput from '../components/GeminiApiKeyInput';

const ProductImageGenPage = () => {
    // Product Photo Studio States
    const [uploadedImage, setUploadedImage] = useState(null); // Primary image for preview
    const [referenceImages, setReferenceImages] = useState([]); // Array of { data: base64, mimeType: string }
    const [uploadedImageBase64, setUploadedImageBase64] = useState(null); // Keep for backward compatibility/primary
    const [uploadedMimeType, setUploadedMimeType] = useState(null); // Keep for backward compatibility/primary
    const [prompt, setPrompt] = useState('');
    const [model, setModel] = useState('gemini-flash');
    const [aspectRatio, setAspectRatio] = useState('1:1');
    const [cameraAngle, setCameraAngle] = useState('');
    const [backgroundStyle, setBackgroundStyle] = useState('');
    const [platform, setPlatform] = useState('');
    const [negativePrompt, setNegativePrompt] = useState('');
    const [loading, setLoading] = useState(false);
    const [enhancing, setEnhancing] = useState(false);
    const [resultImage, setResultImage] = useState(null);
    const [error, setError] = useState('');
    const [history, setHistory] = useState([]);

    // Bulk Mode States
    const [isBulkMode, setIsBulkMode] = useState(false);
    const [selectedAngles, setSelectedAngles] = useState([]);

    const [bulkProgress, setBulkProgress] = useState('');
    const [packCustomization, setPackCustomization] = useState({});

    // Infographic Studio States
    const [activeTab, setActiveTab] = useState('studio'); // 'studio', 'pack', 'infographic'
    const [infographicDetails, setInfographicDetails] = useState('');
    const [infographicAspectRatio, setInfographicAspectRatio] = useState('9:16');
    const [infographicLoading, setInfographicLoading] = useState(false);
    const [infographicResult, setInfographicResult] = useState(null);
    const [infographicError, setInfographicError] = useState('');

    const [geminiApiKey, setGeminiApiKey] = useState('');
    const [showImageModal, setShowImageModal] = useState(false);
    const [modalImage, setModalImage] = useState(null);
    const [showSettings, setShowSettings] = useState(false);

    // Prompt Library States
    const [promptLibrary, setPromptLibrary] = useState([]);
    const [showPromptLibrary, setShowPromptLibrary] = useState(false);
    const [promptLibrarySearch, setPromptLibrarySearch] = useState('');
    const [promptLibraryCategoryFilter, setPromptLibraryCategoryFilter] = useState('');
    const [savePromptFormOpen, setSavePromptFormOpen] = useState(false);
    const [savePromptName, setSavePromptName] = useState('');
    const [savePromptCategory, setSavePromptCategory] = useState('');
    const [promptSavedFlash, setPromptSavedFlash] = useState(false);

    // Infographic analyzer
    const [analyzingInfographic, setAnalyzingInfographic] = useState(false);

    const fileInputRef = useRef(null);

    // Camera angle templates
    const cameraAngles = {
        '': '-- None --',
        'eye-level': 'Eye Level - Straight on, natural perspective',
        'top-down': 'Top Down - Bird\'s eye view, flat lay style',
        'low-angle': 'Low Angle - Shot from below, dramatic perspective',
        'three-quarter': '3/4 View - Angled view showing depth',
        'macro': 'Macro - Extreme close-up, detail shot',
        '45-degree': '45° Angle - Classic product photography angle'
    };

    // Background/Style templates
    const backgroundStyles = {
        '': '-- None --',
        'white-studio': 'Clean White Background - Professional studio lighting',
        'black-luxury': 'Black Luxury - Premium dark background with dramatic lighting',
        'lifestyle': 'Lifestyle Setting - Natural environment, in-use context',
        'minimalist': 'Minimalist - Simple, clean, modern aesthetic',
        'gradient': 'Gradient Background - Smooth color transition',
        'wood-texture': 'Wood Texture - Warm, natural wooden surface',
        'marble': 'Marble Surface - Elegant marble backdrop',
        'outdoor': 'Outdoor Natural - Natural outdoor lighting and setting',
        'neon': 'Neon/Cyberpunk - High contrast, colorful neon lighting',
        'pastel': 'Pastel Studio - Soft, dreamy pastel colors'
    };

    // Platform Presets based on ecomImgGuide.md
    const platformPresets = {
        '': { label: 'None (Custom)', aspectRatio: '1:1', promptSuffix: '' },
        'amazon': {
            label: 'Amazon (Strict)',
            aspectRatio: '1:1',
            promptSuffix: ' on a pure white background (RGB 255, 255, 255). Product must occupy 85% of the frame. No text, props, or watermarks.',
            lockedBackground: 'white-studio'
        },
        'etsy': {
            label: 'Etsy (Aesthetic)',
            aspectRatio: '1:1',
            promptSuffix: ' with a lifestyle background, natural lighting, and aesthetic composition. Center the product with negative space around edges.',
            lockedBackground: 'lifestyle'
        },
        'ebay': {
            label: 'eBay (Clean)',
            aspectRatio: '1:1',
            promptSuffix: ' on a white or neutral grey background. Clean and professional.',
            lockedBackground: 'white-studio'
        },
        'tiktok': {
            label: 'TikTok (Social)',
            aspectRatio: '9:16',
            promptSuffix: ' in a vertical 9:16 format, lifestyle setting, authentic and engaging style suitable for social media.',
            lockedBackground: 'lifestyle'
        }
    };

    const PACK_ITEMS = [
        { id: 'img1', label: 'Image 1', defaultPrompt: 'Professional product photography. High resolution, sharp focus, commercial lighting.' },
        { id: 'img2', label: 'Image 2', defaultPrompt: 'Professional product photography. High resolution, sharp focus, commercial lighting.' },
        { id: 'img3', label: 'Image 3', defaultPrompt: 'Professional product photography. High resolution, sharp focus, commercial lighting.' },
        { id: 'img4', label: 'Image 4', defaultPrompt: 'Professional product photography. High resolution, sharp focus, commercial lighting.' },
        { id: 'img5', label: 'Image 5', defaultPrompt: 'Professional product photography. High resolution, sharp focus, commercial lighting.' },
        { id: 'img6', label: 'Image 6', defaultPrompt: 'Professional product photography. High resolution, sharp focus, commercial lighting.' },
        { id: 'img7', label: 'Image 7', defaultPrompt: 'Professional product photography. High resolution, sharp focus, commercial lighting.' }
    ];

    // Close modal on Escape key
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape' && showImageModal) {
                setShowImageModal(false);
            }
        };
        window.addEventListener('keydown', handleEscape);
        return () => window.removeEventListener('keydown', handleEscape);
    }, [showImageModal]);

    // Auto-open settings if no key is found initially
    useEffect(() => {
        const timer = setTimeout(() => {
            if (!geminiApiKey) setShowSettings(true);
        }, 1000);
        return () => clearTimeout(timer);
    }, [geminiApiKey]);

    useEffect(() => {
        const saved = localStorage.getItem('productImagePromptLibrary');
        if (saved) {
            try {
                setPromptLibrary(JSON.parse(saved));
            } catch (e) {
                console.error("Failed to parse prompt library", e);
            }
        }
    }, []);

    const persistPromptLibrary = (updated) => {
        localStorage.setItem('productImagePromptLibrary', JSON.stringify(updated));
        return updated;
    };

    const saveCurrentPrompt = () => {
        if (!prompt.trim()) {
            setError("Nothing to save — enter a prompt first.");
            return;
        }
        const newEntry = {
            id: Date.now(),
            timestamp: new Date().toISOString(),
            name: savePromptName.trim(),
            category: savePromptCategory.trim(),
            prompt: prompt.trim()
        };
        setPromptLibrary(prev => persistPromptLibrary([newEntry, ...prev].slice(0, 100)));
        setSavePromptFormOpen(false);
        setSavePromptName('');
        setSavePromptCategory('');
        setPromptSavedFlash(true);
        setTimeout(() => setPromptSavedFlash(false), 2000);
    };

    const loadPromptFromLibrary = (item) => {
        if (prompt.trim() && !window.confirm("Replace the current prompt with the saved one?")) {
            return;
        }
        setPrompt(item.prompt);
        setShowPromptLibrary(false);
    };

    const deletePromptLibraryItem = (id) => {
        setPromptLibrary(prev => persistPromptLibrary(prev.filter(it => it.id !== id)));
    };

    const clearPromptLibrary = () => {
        if (window.confirm('Clear the entire prompt library? This cannot be undone.')) {
            setPromptLibrary([]);
            localStorage.removeItem('productImagePromptLibrary');
        }
    };

    const analyzeInfographicImage = async () => {
        if (!geminiApiKey) {
            setInfographicError("Please enter your Gemini API key first.");
            return;
        }
        if (!uploadedImageBase64 || !uploadedMimeType) {
            setInfographicError("Please upload a reference image first.");
            return;
        }
        if (infographicDetails.trim() && !window.confirm("Replace the existing infographic details with AI-generated text from the image?")) {
            return;
        }

        setAnalyzingInfographic(true);
        setInfographicError('');

        try {
            const analyzePrompt = `Analyze this product image and produce content for a product infographic. Output EXACTLY in this format:

Title: <short punchy product title, max 6 words>
• <key feature or benefit, short phrase>
• <key feature or benefit, short phrase>
• <key feature or benefit, short phrase>
• <key feature or benefit, short phrase>

Rules:
- Exactly 4 bullet points.
- Each bullet is a concise benefit or spec, 2-6 words, no full sentences.
- Base everything on what is actually visible in the image. Do not invent prices, warranties, or specs you cannot see.
- No emojis, no extra commentary, no markdown. Return only the title line and the bullet lines.`;

            const response = await fetch(
                `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash:generateContent?key=${geminiApiKey}`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        contents: [{
                            parts: [
                                { text: analyzePrompt },
                                { inline_data: { mime_type: uploadedMimeType, data: uploadedImageBase64 } }
                            ]
                        }]
                    })
                }
            );

            if (!response.ok) throw new Error(`API Error: ${response.statusText}`);

            const data = await response.json();
            const text = data.candidates[0].content.parts[0].text.trim();
            setInfographicDetails(text);
        } catch (err) {
            console.error(err);
            setInfographicError("Failed to analyze image. Please check your API key and try again.");
        } finally {
            setAnalyzingInfographic(false);
        }
    };

    const handleImageUpload = (e, itemId = null) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;

        files.forEach(file => {
            const reader = new FileReader();
            reader.onloadend = () => {
                const dataUrl = reader.result;
                const parts = dataUrl.split(',');
                const mimeType = parts[0].match(/:(.*?);/)[1];
                const base64Data = parts[1];

                if (itemId) {
                    // Handle specific item upload for Amazon Pack
                    setPackCustomization(prev => ({
                        ...prev,
                        [itemId]: {
                            ...prev[itemId],
                            image: {
                                data: base64Data,
                                mimeType: mimeType,
                                preview: dataUrl
                            }
                        }
                    }));
                } else {
                    // Handle general upload
                    // If it's the first image, set as primary
                    if (!uploadedImage) {
                        setUploadedImage(dataUrl);
                        setUploadedImageBase64(base64Data);
                        setUploadedMimeType(mimeType);
                    }

                    // Add to reference images
                    setReferenceImages(prev => [...prev, {
                        id: Date.now() + Math.random(),
                        data: base64Data,
                        mimeType: mimeType,
                        preview: dataUrl
                    }]);
                }
            };
            reader.readAsDataURL(file);
        });
    };

    const removeImage = (id) => {
        setReferenceImages(prev => {
            const newImages = prev.filter(img => img.id !== id);
            // If we removed the primary image, update primary to the next available or null
            if (newImages.length > 0) {
                setUploadedImage(newImages[0].preview);
                setUploadedImageBase64(newImages[0].data);
                setUploadedMimeType(newImages[0].mimeType);
            } else {
                setUploadedImage(null);
                setUploadedImageBase64(null);
                setUploadedMimeType(null);
                if (fileInputRef.current) fileInputRef.current.value = '';
            }
            return newImages;
        });
    };

    const openImageModal = (imageSrc) => {
        setModalImage(imageSrc);
        setShowImageModal(true);
    };

    const toggleAngle = (angleKey) => {
        if (!angleKey) return;
        setSelectedAngles(prev => {
            if (prev.includes(angleKey)) return prev.filter(k => k !== angleKey);
            if (prev.length >= 4) return prev; // Limit max selection
            return [...prev, angleKey];
        });
    };

    const handlePlatformChange = (newPlatform) => {
        setPlatform(newPlatform);
        const preset = platformPresets[newPlatform];
        if (preset) {
            if (preset.aspectRatio) setAspectRatio(preset.aspectRatio);
            if (preset.lockedBackground) setBackgroundStyle(preset.lockedBackground);
        }
    };

    const enhancePrompt = async () => {
        if (!prompt.trim()) {
            setError("Please enter a prompt first");
            return;
        }

        setEnhancing(true);
        setError('');

        try {
            if (!geminiApiKey) {
                setError("Please enter your Gemini API key first");
                setShowSettings(true);
                setEnhancing(false);
                return;
            }
            const response = await fetch(
                `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash:generateContent?key=${geminiApiKey}`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        contents: [{
                            parts: [{
                                text: `You are a professional product photographer. Enhance this prompt to describe a high-end, commercial product shot. Keep the core subject the same, but improve lighting, composition, and mood descriptions. Concise (2-3 sentences). Original prompt: "${prompt}"`
                            }]
                        }]
                    })
                }
            );

            if (!response.ok) throw new Error('Failed to enhance prompt');
            const data = await response.json();
            const enhanced = data.candidates[0].content.parts[0].text.trim();
            setPrompt(enhanced);
        } catch (err) {
            console.error(err);
            setError("Failed to enhance prompt");
        } finally {
            setEnhancing(false);
        }
    };

    const getModelEndpoint = (m) => {
        const endpoints = {
            'gemini-flash': 'gemini-2.5-flash-image',
            'nano-banana-2': 'gemini-3.1-flash-image-preview',
            'gemini-pro': 'gemini-3-pro-image-preview'
        };
        return endpoints[m] || 'gemini-2.5-flash-image';
    };

    const generateSingleImage = async (overrideAngle = null) => {
        const currentAngle = overrideAngle || cameraAngle;

        const modelEndpoint = getModelEndpoint(model);

        const apiUrl = `https://generativelanguage.googleapis.com/v1beta/models/${modelEndpoint}:generateContent?key=${geminiApiKey}`;

        let fullPrompt = "";

        // Add multi-image context if applicable
        if (referenceImages.length > 1) {
            fullPrompt += "CONTEXT: The attached images are reference shots of the SAME single product from different angles. Use them to understand the 3D shape, materials, and details. ";
            fullPrompt += "TASK: Generate a SINGLE, unified image of this ONE product in the specified setting. Do not create a collage, grid, or composite. Do not show multiple versions of the product. ";
        }

        fullPrompt += prompt;

        // Add camera angle if selected
        if (currentAngle && cameraAngles[currentAngle]) {
            fullPrompt += `. Camera Angle: ${cameraAngles[currentAngle]}`;
        }

        // Add background style if selected
        if (backgroundStyle && backgroundStyles[backgroundStyle]) {
            fullPrompt += `. Background/Style: ${backgroundStyles[backgroundStyle]}`;
        }

        // Add Platform specific instructions
        if (platform && platformPresets[platform]) {
            fullPrompt += `. ${platformPresets[platform].promptSuffix}`;
        }

        if (negativePrompt) {
            fullPrompt += ` Do not include: ${negativePrompt}.`;
        }

        // Prepare image parts for all reference images
        const imageParts = referenceImages.map(img => ({
            inlineData: {
                mimeType: img.mimeType,
                data: img.data
            }
        }));

        // If no reference images (fallback), use the primary one
        if (imageParts.length === 0 && uploadedImageBase64) {
            imageParts.push({
                inlineData: {
                    mimeType: uploadedMimeType,
                    data: uploadedImageBase64
                }
            });
        }

        const payload = {
            contents: [{
                parts: [
                    { text: fullPrompt },
                    ...imageParts
                ]
            }],
            generationConfig: {
                responseModalities: ["TEXT", "IMAGE"],
                imageConfig: {
                    aspectRatio: aspectRatio
                }
            }
        };

        const response = await fetch(apiUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
        });

        if (!response.ok) throw new Error(`API Error: ${response.statusText}`);

        const result = await response.json();
        const imagePart = result?.candidates?.[0]?.content?.parts?.find(p => p.inlineData);
        const base64Data = imagePart?.inlineData?.data;

        if (base64Data) {
            return `data:image/png;base64,${base64Data}`;
        } else {
            throw new Error("No image data found");
        }
    };

    const generateAmazonPack = async () => {
        if (!geminiApiKey) {
            setError("Please enter your Gemini API key first");
            setShowSettings(true);
            return;
        }

        setLoading(true);
        setError('');
        setResultImage(null);
        setBulkProgress('');

        const packPrompts = PACK_ITEMS.map(item => {
            let specificPrompt = "";
            const customImage = packCustomization[item.id]?.image;
            // Use custom image if available, otherwise check reference images
            const hasReference = customImage || referenceImages.length > 0 || uploadedImageBase64;

            if (hasReference) {
                specificPrompt += "CONTEXT: Use the attached reference image(s) to understand the product. ";
                specificPrompt += "TASK: Generate a SINGLE high-quality product image. ";
            }

            // Use user context as the main driver, fallback to default if empty (though default is now generic)
            const userContext = packCustomization[item.id]?.context || "";
            if (userContext) {
                specificPrompt += userContext;
            } else {
                specificPrompt += item.defaultPrompt;
            }

            // Append negative prompt if provided
            if (packCustomization[item.id]?.negative) {
                specificPrompt += ` Do not include: ${packCustomization[item.id].negative}.`;
            }

            return {
                id: item.id,
                type: item.label,
                prompt: specificPrompt,
                aspectRatio: '1:1',
                customImage: customImage
            };
        });

        try {
            let completed = 0;
            for (const item of packPrompts) {
                setBulkProgress(`Generating ${item.type} (${completed + 1}/7)...`);



                // We construct a specific prompt for this item
                const specificPrompt = item.prompt;

                try {
                    // We need to call the API directly here to avoid state conflicts with the shared generateSingleImage function which uses state
                    const modelEndpoint = getModelEndpoint(model);
                    const apiUrl = `https://generativelanguage.googleapis.com/v1beta/models/${modelEndpoint}:generateContent?key=${geminiApiKey}`;

                    // Prepare image parts
                    let imageParts = [];

                    // 1. Check for specific item image
                    if (item.customImage) {
                        imageParts.push({
                            inlineData: {
                                mimeType: item.customImage.mimeType,
                                data: item.customImage.data
                            }
                        });
                    }
                    // 2. Fallback to global reference images
                    else if (referenceImages.length > 0) {
                        imageParts = referenceImages.map(img => ({
                            inlineData: {
                                mimeType: img.mimeType,
                                data: img.data
                            }
                        }));
                    }
                    // 3. Fallback to primary uploaded image
                    else if (uploadedImageBase64) {
                        imageParts.push({
                            inlineData: {
                                mimeType: uploadedMimeType,
                                data: uploadedImageBase64
                            }
                        });
                    }

                    const payload = {
                        contents: [{
                            parts: [
                                { text: specificPrompt },
                                ...imageParts
                            ]
                        }],
                        generationConfig: {
                            responseModalities: ["TEXT", "IMAGE"],
                            imageConfig: {
                                aspectRatio: item.aspectRatio
                            }
                        }
                    };

                    const response = await fetch(apiUrl, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(payload)
                    });

                    if (!response.ok) throw new Error(`API Error: ${response.statusText}`);

                    const result = await response.json();
                    const imagePart = result?.candidates?.[0]?.content?.parts?.find(p => p.inlineData);
                    const base64Data = imagePart?.inlineData?.data;

                    if (base64Data) {
                        const newImage = `data:image/png;base64,${base64Data}`;
                        setResultImage(newImage); // Show latest
                        setHistory(prev => [{
                            id: Date.now() + Math.random(),
                            url: newImage,
                            type: 'amazon-pack',
                            timestamp: new Date().toLocaleTimeString(),
                            details: item.type
                        }, ...prev]);
                    }
                    completed++;
                } catch (err) {
                    console.error(`Failed to generate ${item.type}`, err);
                }
            }
            setBulkProgress('Amazon Pack generation complete!');
        } catch (err) {
            console.error(err);
            setError(err.message || "Failed to generate pack");
        } finally {
            setLoading(false);
            setBulkProgress('');
        }
    };

    const generateProductImage = async () => {
        if (!uploadedImageBase64 || !prompt.trim()) {
            setError("Please upload an image and provide a prompt");
            return;
        }

        if (isBulkMode && selectedAngles.length === 0) {
            setError("Please select at least one camera angle for bulk generation");
            return;
        }

        if (!geminiApiKey) {
            setError("Please enter your Gemini API key first");
            setShowSettings(true);
            return;
        }

        setLoading(true);
        setError('');
        setResultImage(null);
        setBulkProgress('');

        try {
            if (isBulkMode) {
                let completed = 0;
                for (const angle of selectedAngles) {
                    setBulkProgress(`Generating ${cameraAngles[angle].split(' - ')[0]} (${completed + 1}/${selectedAngles.length})...`);
                    try {
                        const newImage = await generateSingleImage(angle);
                        setResultImage(newImage); // Show latest
                        setHistory(prev => [{
                            id: Date.now() + Math.random(),
                            url: newImage,
                            type: 'product',
                            timestamp: new Date().toLocaleTimeString(),
                            details: `Angle: ${cameraAngles[angle].split(' - ')[0]}`
                        }, ...prev]);
                        completed++;
                    } catch (err) {
                        console.error(`Failed to generate for angle ${angle}`, err);
                        // Continue to next
                    }
                }
                setBulkProgress('Bulk generation complete!');
            } else {
                const newImage = await generateSingleImage();
                setResultImage(newImage);
                setHistory(prev => [{
                    id: Date.now(),
                    url: newImage,
                    type: 'product',
                    timestamp: new Date().toLocaleTimeString()
                }, ...prev]);
            }
        } catch (err) {
            console.error(err);
            setError(err.message || "Failed to generate image");
        } finally {
            setLoading(false);
            setBulkProgress('');
        }
    };

    const generateInfographic = async () => {
        if (!uploadedImageBase64 || !infographicDetails.trim()) {
            setInfographicError("Please upload an image and provide infographic details");
            return;
        }

        setInfographicLoading(true);
        setInfographicError('');
        setInfographicResult(null);

        try {
            if (!geminiApiKey) {
                setInfographicError("Please enter your Gemini API key first");
                setShowSettings(true);
                setInfographicLoading(false);
                return;
            }

            const modelEndpoint = getModelEndpoint(model);

            const apiUrl = `https://generativelanguage.googleapis.com/v1beta/models/${modelEndpoint}:generateContent?key=${geminiApiKey}`;

            let infographicPrompt = "";
            if (referenceImages.length > 1) {
                infographicPrompt += "CONTEXT: The attached images show the SAME single product. Use them to understand the product. ";
            }
            infographicPrompt += `Create a professional product infographic for this product. Include the following details in an attractive, modern layout: ${infographicDetails}. Make it visually appealing with clear typography, icons, and a professional design suitable for social media or marketing materials. Create a cohesive design, not just a collage of the input images.`;

            // Prepare image parts for all reference images
            const imageParts = referenceImages.map(img => ({
                inlineData: {
                    mimeType: img.mimeType,
                    data: img.data
                }
            }));

            // If no reference images (fallback), use the primary one
            if (imageParts.length === 0 && uploadedImageBase64) {
                imageParts.push({
                    inlineData: {
                        mimeType: uploadedMimeType,
                        data: uploadedImageBase64
                    }
                });
            }

            const payload = {
                contents: [{
                    parts: [
                        { text: infographicPrompt },
                        ...imageParts
                    ]
                }],
                generationConfig: {
                    responseModalities: ["TEXT", "IMAGE"],
                    imageConfig: {
                        aspectRatio: infographicAspectRatio
                    }
                }
            };

            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (!response.ok) throw new Error(`API Error: ${response.statusText}`);

            const result = await response.json();
            const imagePart = result?.candidates?.[0]?.content?.parts?.find(p => p.inlineData);
            const base64Data = imagePart?.inlineData?.data;

            if (base64Data) {
                const newImage = `data:image/png;base64,${base64Data}`;
                setInfographicResult(newImage);
                setHistory(prev => [{
                    id: Date.now(),
                    url: newImage,
                    type: 'infographic',
                    timestamp: new Date().toLocaleTimeString()
                }, ...prev]);
            } else {
                throw new Error("No image data found");
            }
        } catch (err) {
            console.error(err);
            setInfographicError(err.message || "Failed to generate infographic");
        } finally {
            setInfographicLoading(false);
        }
    };

    const handleDownload = (imageSrc, filename) => {
        if (imageSrc) {
            const link = document.createElement('a');
            link.href = imageSrc;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    return (
        <div className="flex-grow bg-gradient-to-br from-gray-900 via-slate-900 to-black min-h-screen text-slate-200 p-4 md:p-8 animate-fade-in flex flex-col">
            <div className="max-w-7xl mx-auto w-full space-y-6 flex-grow">

                {/* Header & Settings Toggle */}
                <div className="flex justify-between items-center">
                    <div>
                        <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-indigo-400 to-cyan-400 bg-clip-text text-transparent">
                            AI Product Studio
                        </h1>
                        <p className="text-slate-400 text-sm">Professional Shots & Infographics</p>
                    </div>
                    <button
                        onClick={() => setShowSettings(!showSettings)}
                        className={`p-2 rounded-full transition-colors ${showSettings ? 'bg-indigo-600 text-white' : 'bg-slate-800 text-slate-400 hover:text-white'}`}
                        title="API Key Settings"
                        aria-label="Toggle settings"
                    >
                        <Settings size={24} />
                    </button>
                </div>

                {/* Collapsible Settings Panel */}
                {showSettings && (
                    <div className="animate-fade-in">
                        <GeminiApiKeyInput onApiKeyChange={setGeminiApiKey} />
                    </div>
                )}

                {/* Mode Tabs */}
                <div className="flex gap-4 border-b border-slate-700 pb-1 overflow-x-auto">
                    <button
                        onClick={() => setActiveTab('studio')}
                        className={`pb-2 px-4 font-semibold transition-colors whitespace-nowrap ${activeTab === 'studio' ? 'text-indigo-400 border-b-2 border-indigo-400' : 'text-slate-400 hover:text-slate-200'}`}
                    >
                        Photo Studio
                    </button>
                    <button
                        onClick={() => setActiveTab('pack')}
                        className={`pb-2 px-4 font-semibold transition-colors whitespace-nowrap flex items-center gap-2 ${activeTab === 'pack' ? 'text-orange-400 border-b-2 border-orange-400' : 'text-slate-400 hover:text-slate-200'}`}
                    >
                        <Package size={16} /> Amazon Pack
                    </button>
                    <button
                        onClick={() => setActiveTab('infographic')}
                        className={`pb-2 px-4 font-semibold transition-colors whitespace-nowrap ${activeTab === 'infographic' ? 'text-purple-400 border-b-2 border-purple-400' : 'text-slate-400 hover:text-slate-200'}`}
                    >
                        Infographic Creator
                    </button>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">

                    {/* LEFT COLUMN: Inputs */}
                    <div className="space-y-6">

                        {/* Image Upload Area */}
                        <div className={`relative border-2 border-dashed rounded-xl transition-all p-6 flex flex-col items-center justify-center min-h-[250px] ${referenceImages.length > 0 ? 'border-indigo-500/50 bg-slate-800/50' : 'border-slate-600 hover:border-slate-500 bg-slate-800/30'}`}>
                            {referenceImages.length > 0 ? (
                                <div className="w-full">
                                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4">
                                        {referenceImages.map((img) => (
                                            <div key={img.id} className="relative group aspect-square">
                                                <img
                                                    src={img.preview}
                                                    alt="Reference"
                                                    className="w-full h-full object-cover rounded-lg border border-slate-700"
                                                />
                                                <button
                                                    onClick={() => removeImage(img.id)}
                                                    className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity shadow-lg hover:bg-red-600"
                                                    aria-label="Remove image"
                                                >
                                                    <X size={12} />
                                                </button>
                                            </div>
                                        ))}
                                        <label className="flex flex-col items-center justify-center cursor-pointer aspect-square border-2 border-dashed border-slate-600 rounded-lg hover:border-indigo-500 hover:bg-slate-700/30 transition-colors">
                                            <Upload className="w-6 h-6 text-slate-500 mb-1" />
                                            <span className="text-xs text-slate-400">Add More</span>
                                            <input
                                                type="file"
                                                className="hidden"
                                                accept="image/*"
                                                multiple
                                                onChange={handleImageUpload}
                                            />
                                        </label>
                                    </div>
                                    <p className="text-xs text-center text-slate-400">{referenceImages.length} reference image(s) uploaded</p>
                                </div>
                            ) : (
                                <label className="flex flex-col items-center justify-center cursor-pointer w-full h-full">
                                    <Upload className="w-12 h-12 text-slate-500 mb-4" />
                                    <span className="text-lg font-medium text-slate-300 text-center">Upload Reference Images</span>
                                    <span className="text-sm text-slate-500 mt-2">Upload multiple angles for better results</span>
                                    <span className="text-xs text-slate-600 mt-1">PNG, JPG, WEBP</span>
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        className="hidden"
                                        accept="image/*"
                                        multiple
                                        onChange={handleImageUpload}
                                    />
                                </label>
                            )}
                        </div>

                        {/* Studio Controls */}
                        {activeTab === 'studio' && (
                            <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-6 space-y-6 animate-fade-in">
                                <div>
                                    <div className="flex justify-between items-center mb-2 gap-2">
                                        <label className="text-sm font-semibold text-white flex items-center gap-2">
                                            <Sparkles size={16} className="text-indigo-400" />
                                            Describe Result
                                        </label>
                                        <div className="flex items-center gap-3">
                                            <button
                                                onClick={() => setShowPromptLibrary(true)}
                                                className="text-xs flex items-center gap-1 text-indigo-400 hover:text-indigo-300"
                                                title="Browse saved prompts"
                                            >
                                                <BookMarked size={12} />
                                                Library
                                                {promptLibrary.length > 0 && (
                                                    <span className="text-[10px] px-1 rounded bg-indigo-500/20 text-indigo-300">{promptLibrary.length}</span>
                                                )}
                                            </button>
                                            <button
                                                onClick={() => setSavePromptFormOpen(v => !v)}
                                                disabled={!prompt.trim()}
                                                className="text-xs flex items-center gap-1 text-indigo-400 hover:text-indigo-300 disabled:opacity-50"
                                                title="Save current prompt to library"
                                            >
                                                {promptSavedFlash ? <Save size={12} /> : <Save size={12} />}
                                                {promptSavedFlash ? 'Saved' : 'Save'}
                                            </button>
                                            <button
                                                onClick={enhancePrompt}
                                                disabled={enhancing || !prompt.trim()}
                                                className="text-xs flex items-center gap-1 text-indigo-400 hover:text-indigo-300 disabled:opacity-50"
                                            >
                                                {enhancing ? <Loader2 className="animate-spin" size={12} /> : <Wand2 size={12} />}
                                                Enhance
                                            </button>
                                        </div>
                                    </div>
                                    <textarea
                                        value={prompt}
                                        onChange={(e) => setPrompt(e.target.value)}
                                        className="w-full p-3 bg-slate-900/50 border border-slate-600 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none resize-none text-slate-200"
                                        placeholder="e.g., A professional shot on a marble table with soft morning light..."
                                        style={{ minHeight: '100px' }}
                                    />
                                    {savePromptFormOpen && (
                                        <div className="mt-3 p-3 bg-indigo-500/5 border border-indigo-500/20 rounded-lg space-y-2">
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                                <input
                                                    type="text"
                                                    value={savePromptName}
                                                    onChange={(e) => setSavePromptName(e.target.value)}
                                                    placeholder="Name (optional, e.g. Studio white BG)"
                                                    className="w-full p-2 text-xs bg-slate-900/50 border border-slate-600 rounded focus:ring-2 focus:ring-indigo-500 outline-none text-slate-200 placeholder-slate-500"
                                                />
                                                <input
                                                    type="text"
                                                    value={savePromptCategory}
                                                    onChange={(e) => setSavePromptCategory(e.target.value)}
                                                    placeholder="Category (e.g. Pendants / necklace)"
                                                    className="w-full p-2 text-xs bg-slate-900/50 border border-slate-600 rounded focus:ring-2 focus:ring-indigo-500 outline-none text-slate-200 placeholder-slate-500"
                                                />
                                            </div>
                                            <div className="flex gap-2 justify-end">
                                                <button
                                                    onClick={() => { setSavePromptFormOpen(false); setSavePromptName(''); setSavePromptCategory(''); }}
                                                    className="text-xs font-medium text-slate-300 px-3 py-1 rounded bg-slate-700 hover:bg-slate-600 transition-colors"
                                                >
                                                    Cancel
                                                </button>
                                                <button
                                                    onClick={saveCurrentPrompt}
                                                    className="flex items-center gap-1 text-xs font-medium text-white px-3 py-1 rounded bg-indigo-600 hover:bg-indigo-500 transition-colors"
                                                >
                                                    <Save size={12} /> Save
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Platform Preset</label>
                                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                        {Object.entries(platformPresets).map(([key, preset]) => (
                                            <button
                                                key={key}
                                                onClick={() => handlePlatformChange(key)}
                                                className={`text-xs p-2 rounded-lg border transition-all text-left ${platform === key
                                                    ? 'bg-indigo-600/20 border-indigo-500 text-indigo-200'
                                                    : 'bg-slate-800 border-slate-700 text-slate-400 hover:border-slate-500'
                                                    }`}
                                            >
                                                {preset.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label htmlFor="model-select" className="block text-xs font-bold text-slate-400 uppercase mb-1">Model</label>
                                        <select
                                            id="model-select"
                                            value={model}
                                            onChange={(e) => setModel(e.target.value)}
                                            className="w-full p-2 bg-slate-900 border border-slate-600 rounded-lg text-sm text-slate-200 focus:border-indigo-500 outline-none"
                                        >
                                            <option value="gemini-flash">Nano Banana (Gemini 2.5 Flash) — $0.039/img</option>
                                            <option value="nano-banana-2">Nano Banana 2 (Gemini 3.1 Flash) — $0.067/img</option>
                                            <option value="gemini-pro">Nano Banana Pro (Gemini 3 Pro) — $0.134/img</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label htmlFor="aspect-ratio-select" className="block text-xs font-bold text-slate-400 uppercase mb-1">Aspect Ratio</label>
                                        <select
                                            id="aspect-ratio-select"
                                            value={aspectRatio}
                                            onChange={(e) => setAspectRatio(e.target.value)}
                                            className="w-full p-2 bg-slate-900 border border-slate-600 rounded-lg text-sm text-slate-200 focus:border-indigo-500 outline-none"
                                        >
                                            <option value="1:1">1:1 (Square)</option>
                                            <option value="16:9">16:9 (Landscape)</option>
                                            <option value="9:16">9:16 (Portrait)</option>
                                            <option value="4:3">4:3 (Standard)</option>
                                        </select>
                                    </div>

                                    <div className="col-span-2 space-y-2">
                                        <label className="text-xs font-bold text-slate-400 uppercase">Camera Angle</label>
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => {
                                                    setIsBulkMode(!isBulkMode);
                                                }}
                                                className={`text-xs flex items-center gap-1 px-2 py-1 rounded-full transition-colors ${isBulkMode ? 'bg-indigo-600 text-white' : 'bg-slate-700 text-slate-400 hover:text-white'}`}
                                            >
                                                <Layers size={12} />
                                                Bulk Mode {isBulkMode ? 'ON' : 'OFF'}
                                            </button>
                                        </div>


                                        {isBulkMode ? (
                                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 bg-slate-900/50 p-3 rounded-lg border border-slate-600">
                                                {Object.entries(cameraAngles).filter(([k]) => k !== '').map(([key, value]) => (
                                                    <button
                                                        key={key}
                                                        onClick={() => toggleAngle(key)}
                                                        className={`text-xs p-2 rounded-lg border transition-all text-left ${selectedAngles.includes(key) ? 'bg-indigo-600/20 border-indigo-500 text-indigo-200' : 'bg-slate-800 border-slate-700 text-slate-400 hover:border-slate-500'}`}
                                                    >
                                                        {value.split(' - ')[0]}
                                                    </button>
                                                ))}
                                                <p className="col-span-full text-[10px] text-slate-500 mt-1 text-center">
                                                    Select up to 4 angles. 1 image generated per angle.
                                                </p>
                                            </div>
                                        ) : (
                                            <select
                                                value={cameraAngle}
                                                onChange={(e) => setCameraAngle(e.target.value)}
                                                className="w-full p-2 bg-slate-900 border border-slate-600 rounded-lg text-sm text-slate-200 focus:border-indigo-500 outline-none"
                                            >
                                                {Object.entries(cameraAngles).map(([key, value]) => (
                                                    <option key={key} value={key}>{value}</option>
                                                ))}
                                            </select>
                                        )}
                                    </div>

                                    <div className="col-span-2">
                                        <label htmlFor="background-style-select" className="block text-xs font-bold text-slate-400 uppercase mb-1">Background Style</label>
                                        <select
                                            id="background-style-select"
                                            value={backgroundStyle}
                                            onChange={(e) => setBackgroundStyle(e.target.value)}
                                            className="w-full p-2 bg-slate-900 border border-slate-600 rounded-lg text-sm text-slate-200 focus:border-indigo-500 outline-none"
                                        >
                                            {Object.entries(backgroundStyles).map(([key, value]) => (
                                                <option key={key} value={key}>{value}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="col-span-2">
                                        <label htmlFor="negative-prompt-input" className="block text-xs font-bold text-slate-400 uppercase mb-1">Negative Prompt</label>
                                        <input
                                            id="negative-prompt-input"
                                            type="text"
                                            value={negativePrompt}
                                            onChange={(e) => setNegativePrompt(e.target.value)}
                                            className="w-full p-2 bg-slate-900 border border-slate-600 rounded-lg text-sm text-slate-200 focus:border-indigo-500 outline-none"
                                            placeholder="e.g., blur, distortion, bad lighting"
                                        />
                                    </div>
                                </div>

                                <button
                                    onClick={generateProductImage}
                                    disabled={loading || !uploadedImageBase64}
                                    className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white font-bold py-3 rounded-xl shadow-lg transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                                >
                                    {loading ? (
                                        <>
                                            <Loader2 className="animate-spin" size={20} />
                                            {bulkProgress || 'Generating...'}
                                        </>
                                    ) : (
                                        <>
                                            {isBulkMode ? <Layers size={20} /> : <Camera size={20} />}
                                            {isBulkMode ? `Generate ${selectedAngles.length > 0 ? selectedAngles.length : ''} Variations` : 'Generate Photo'}
                                        </>
                                    )}
                                </button>
                                {error && <div className="text-red-400 text-sm text-center bg-red-500/10 p-2 rounded">{error}</div>}
                            </div>
                        )}

                        {/* Amazon Pack Controls */}
                        {activeTab === 'pack' && (
                            <div className="bg-slate-800/50 rounded-xl border border-orange-500/30 p-6 space-y-6 animate-fade-in">
                                <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-3 text-xs text-orange-200">
                                    <p className="font-bold mb-1 flex items-center gap-2"><Package size={14} /> Amazon Listing Pack Generator</p>
                                    <p className="opacity-80">Generate 7 unique images for your listing. Upload a specific reference image for each slot if needed, or leave blank to use the main/global reference images.</p>
                                </div>

                                <div className="space-y-4 max-h-[400px] sm:max-h-[600px] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-transparent">
                                    {PACK_ITEMS.map(item => (
                                        <div key={item.id} className="bg-slate-900/50 p-4 rounded-lg border border-slate-700 space-y-3">
                                            <div className="flex justify-between items-center">
                                                <div className="font-semibold text-sm text-slate-300">{item.label}</div>
                                                {packCustomization[item.id]?.image && (
                                                    <span className="text-[10px] bg-green-500/20 text-green-400 px-2 py-0.5 rounded-full">Image Uploaded</span>
                                                )}
                                            </div>

                                            {/* Per-item Image Upload */}
                                            <div className="flex items-center gap-3">
                                                <div className="relative w-16 h-16 bg-slate-800 rounded border border-slate-600 flex-shrink-0 overflow-hidden group">
                                                    {packCustomization[item.id]?.image ? (
                                                        <>
                                                            <img src={packCustomization[item.id].image.preview} alt="Ref" className="w-full h-full object-cover" />
                                                            <button
                                                                onClick={() => setPackCustomization(prev => {
                                                                    const newState = { ...prev };
                                                                    delete newState[item.id].image;
                                                                    return newState;
                                                                })}
                                                                className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-white"
                                                            >
                                                                <X size={16} />
                                                            </button>
                                                        </>
                                                    ) : (
                                                        <label className="flex flex-col items-center justify-center w-full h-full cursor-pointer hover:bg-slate-700 transition-colors">
                                                            <Upload size={16} className="text-slate-500" />
                                                            <input type="file" className="hidden" accept="image/*" onChange={(e) => handleImageUpload(e, item.id)} />
                                                        </label>
                                                    )}
                                                </div>
                                                <div className="flex-grow space-y-2">
                                                    <input
                                                        placeholder="Describe this image (e.g. 'Main white background shot', 'Lifestyle in kitchen')"
                                                        className="w-full p-2 bg-slate-800 border border-slate-600 rounded text-xs text-slate-200 focus:border-orange-500 outline-none"
                                                        value={packCustomization[item.id]?.context || ''}
                                                        onChange={(e) => setPackCustomization(prev => ({
                                                            ...prev,
                                                            [item.id]: { ...prev[item.id], context: e.target.value }
                                                        }))}
                                                    />
                                                    <input
                                                        placeholder="Negative prompt (e.g. 'no text', 'no hands')"
                                                        className="w-full p-2 bg-slate-800 border border-slate-600 rounded text-xs text-slate-200 focus:border-orange-500 outline-none"
                                                        value={packCustomization[item.id]?.negative || ''}
                                                        onChange={(e) => setPackCustomization(prev => ({
                                                            ...prev,
                                                            [item.id]: { ...prev[item.id], negative: e.target.value }
                                                        }))}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <button
                                    onClick={generateAmazonPack}
                                    disabled={loading}
                                    className="w-full bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-500 hover:to-red-500 text-white font-bold py-3 rounded-xl shadow-lg transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                                >
                                    {loading ? (
                                        <>
                                            <Loader2 className="animate-spin" size={20} />
                                            {bulkProgress || 'Generating Pack...'}
                                        </>
                                    ) : (
                                        <>
                                            <Package size={20} />
                                            Generate Amazon Pack (7 Images)
                                        </>
                                    )}
                                </button>
                                {error && <div className="text-red-400 text-sm text-center bg-red-500/10 p-2 rounded">{error}</div>}
                            </div>
                        )}

                        {/* Infographic Controls */}
                        {activeTab === 'infographic' && (
                            <div className="bg-slate-800/50 rounded-xl border border-purple-700/50 p-6 space-y-6 animate-fade-in">
                                <div>
                                    <div className="flex justify-between items-center mb-2 gap-2">
                                        <label htmlFor="infographic-details-textarea" className="text-sm font-semibold text-white">Infographic Details</label>
                                        <button
                                            onClick={analyzeInfographicImage}
                                            disabled={analyzingInfographic || !uploadedImageBase64 || !geminiApiKey}
                                            title={!uploadedImageBase64 ? 'Upload a reference image first' : !geminiApiKey ? 'Enter your Gemini API key first' : 'Generate infographic details from the uploaded image'}
                                            className="text-xs flex items-center gap-1 text-purple-400 hover:text-purple-300 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            {analyzingInfographic ? (
                                                <><Loader2 className="animate-spin" size={12} /> Analyzing...</>
                                            ) : (
                                                <><Sparkles size={12} /> Analyze Image</>
                                            )}
                                        </button>
                                    </div>
                                    <textarea
                                        id="infographic-details-textarea"
                                        value={infographicDetails}
                                        onChange={(e) => setInfographicDetails(e.target.value)}
                                        className="w-full p-3 bg-slate-900/50 border border-slate-600 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none resize-none text-slate-200"
                                        placeholder="e.g., Title: Super Gadget&#10;• 24h Battery&#10;• Waterproof&#10;• $99"
                                        style={{ minHeight: '120px' }}
                                    />
                                </div>
                                <div>
                                    <label htmlFor="infographic-aspect-ratio-select" className="block text-xs font-bold text-slate-400 uppercase mb-1">Size</label>
                                    <select
                                        id="infographic-aspect-ratio-select"
                                        value={infographicAspectRatio}
                                        onChange={(e) => setInfographicAspectRatio(e.target.value)}
                                        className="w-full p-2 bg-slate-900 border border-slate-600 rounded-lg text-sm text-slate-200 focus:border-purple-500 outline-none"
                                    >
                                        <option value="9:16">9:16 (Story/TikTok)</option>
                                        <option value="4:5">4:5 (Instagram Portrait)</option>
                                        <option value="1:1">1:1 (Square Post)</option>
                                        <option value="16:9">16:9 (Presentation)</option>
                                    </select>
                                </div>
                                <button
                                    onClick={generateInfographic}
                                    disabled={infographicLoading || !uploadedImageBase64}
                                    className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white font-bold py-3 rounded-xl shadow-lg transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                                >
                                    {infographicLoading ? <Loader2 className="animate-spin" size={20} /> : <LayoutTemplate size={20} />}
                                    Generate Infographic
                                </button>
                                {infographicError && <div className="text-red-400 text-sm text-center bg-red-500/10 p-2 rounded">{infographicError}</div>}
                            </div>
                        )}
                    </div>

                    {/* RIGHT COLUMN: Output */}
                    <div className="flex flex-col gap-6">
                        <div className="bg-slate-950 rounded-2xl border border-slate-800 p-1 shadow-2xl flex-grow min-h-[300px] sm:min-h-[500px] flex items-center justify-center relative overflow-hidden group">
                            {(activeTab === 'studio' ? resultImage : activeTab === 'pack' ? resultImage : infographicResult) ? (
                                <>
                                    <img
                                        src={activeTab === 'studio' ? resultImage : activeTab === 'pack' ? resultImage : infographicResult}
                                        alt="Generated Result"
                                        className="max-w-full max-h-[400px] sm:max-h-[600px] object-contain rounded-xl shadow-2xl cursor-pointer"
                                        onClick={() => openImageModal(activeTab === 'studio' ? resultImage : activeTab === 'pack' ? resultImage : infographicResult)}
                                    />
                                    <div className="absolute bottom-4 right-4 flex gap-2 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                                        <button
                                            onClick={() => handleDownload(activeTab === 'studio' ? resultImage : activeTab === 'pack' ? resultImage : infographicResult, 'generated-image.png')}
                                            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-semibold shadow-lg flex items-center gap-2"
                                        >
                                            <Download size={18} />
                                            Download
                                        </button>
                                    </div>
                                </>
                            ) : (
                                <div className="text-center text-slate-600">
                                    <ImageIcon size={64} className="mx-auto mb-4 opacity-20" />
                                    <p className="text-xl font-medium opacity-50">
                                        {activeTab === 'studio' ? 'Ready to Create Photo' : activeTab === 'pack' ? 'Ready to Generate Pack' : 'Ready to Create Infographic'}
                                    </p>
                                    {(loading || infographicLoading) && (
                                        <div className="mt-4 flex items-center justify-center gap-2 text-indigo-400">
                                            <Loader2 className="animate-spin" size={20} />
                                            <span>Creating magic...</span>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Session History */}
                        {history.length > 0 && (
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-slate-400 uppercase flex items-center gap-2">
                                    <History size={14} />
                                    Recent Generations
                                </label>
                                <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-transparent">
                                    {history.map((item) => (
                                        <div key={item.id} className="flex-shrink-0 w-20 sm:w-24 group relative">
                                            <img
                                                src={item.url}
                                                alt="History"
                                                className={`w-full h-20 sm:h-24 object-cover rounded-lg border cursor-pointer transition-colors ${item.type === 'infographic' ? 'border-purple-500/50 hover:border-purple-400' : item.type === 'amazon-pack' ? 'border-orange-500/50 hover:border-orange-400' : 'border-slate-700 hover:border-indigo-500'}`}
                                                onClick={() => {
                                                    if (item.type === 'infographic') {
                                                        setActiveTab('infographic');
                                                        setInfographicResult(item.url);
                                                    } else if (item.type === 'amazon-pack') {
                                                        setActiveTab('pack');
                                                        setResultImage(item.url);
                                                    } else {
                                                        setActiveTab('studio');
                                                        setResultImage(item.url);
                                                    }
                                                }}
                                            />
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    if (window.confirm('Remove this image from history?')) {
                                                        setHistory(prev => prev.filter(h => h.id !== item.id));
                                                    }
                                                }}
                                                className="absolute top-1 right-1 p-1 bg-black/50 text-white rounded-full opacity-100 sm:opacity-0 sm:group-hover:opacity-100 hover:bg-red-500 transition-all"
                                                aria-label="Remove from history"
                                            >
                                                <Trash2 size={10} />
                                            </button>
                                            {item.details && (
                                                <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-[9px] text-white text-center p-0.5 truncate">
                                                    {item.details}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Prompt Library Modal */}
            {showPromptLibrary && (() => {
                const allCategories = Array.from(new Set(promptLibrary.map(it => it.category).filter(Boolean))).sort();
                const q = promptLibrarySearch.trim().toLowerCase();
                const filtered = promptLibrary.filter(it => {
                    if (promptLibraryCategoryFilter && it.category !== promptLibraryCategoryFilter) return false;
                    if (q) {
                        const hay = [it.name, it.category, it.prompt].join(' ').toLowerCase();
                        if (!hay.includes(q)) return false;
                    }
                    return true;
                });

                return (
                    <div
                        className="fixed inset-0 bg-black/70 backdrop-blur-md z-50 flex items-start justify-center p-4 overflow-y-auto animate-fade-in"
                        onClick={() => setShowPromptLibrary(false)}
                    >
                        <div
                            className="w-full max-w-4xl bg-slate-900 rounded-2xl shadow-2xl border border-slate-700 my-8"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="flex justify-between items-center p-6 border-b border-slate-700">
                                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                                    <BookMarked className="text-indigo-400" /> Prompt Library
                                    <span className="text-sm font-normal text-slate-400">{promptLibrary.length} saved</span>
                                </h2>
                                <button
                                    onClick={() => setShowPromptLibrary(false)}
                                    className="p-2 hover:bg-slate-800 rounded-full text-slate-400 hover:text-white transition-colors"
                                    aria-label="Close prompt library"
                                >
                                    <X size={20} />
                                </button>
                            </div>

                            {promptLibrary.length > 0 && (
                                <div className="p-6 border-b border-slate-700 space-y-4">
                                    <div className="flex gap-3 items-center">
                                        <div className="relative flex-grow">
                                            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                                            <input
                                                type="text"
                                                value={promptLibrarySearch}
                                                onChange={(e) => setPromptLibrarySearch(e.target.value)}
                                                placeholder="Search by name, category, or prompt text..."
                                                className="w-full pl-10 pr-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-sm text-slate-200 placeholder-slate-500 focus:ring-2 focus:ring-indigo-500 outline-none"
                                            />
                                        </div>
                                        <button
                                            onClick={clearPromptLibrary}
                                            className="text-xs font-medium text-red-400 hover:bg-red-500/10 border border-red-500/20 px-3 py-2 rounded-lg transition-colors flex items-center gap-1.5 whitespace-nowrap"
                                        >
                                            <Trash2 size={14} /> Clear All
                                        </button>
                                    </div>

                                    {allCategories.length > 0 && (
                                        <div className="flex flex-wrap gap-2 items-center">
                                            <TagIcon size={14} className="text-slate-500" />
                                            <button
                                                onClick={() => setPromptLibraryCategoryFilter('')}
                                                className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${promptLibraryCategoryFilter === ''
                                                    ? 'bg-indigo-500/20 border-indigo-500/40 text-indigo-300'
                                                    : 'bg-slate-800 border-slate-700 text-slate-400 hover:text-slate-200'
                                                    }`}
                                            >
                                                All
                                            </button>
                                            {allCategories.map(cat => (
                                                <button
                                                    key={cat}
                                                    onClick={() => setPromptLibraryCategoryFilter(cat === promptLibraryCategoryFilter ? '' : cat)}
                                                    className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${promptLibraryCategoryFilter === cat
                                                        ? 'bg-indigo-500/20 border-indigo-500/40 text-indigo-300'
                                                        : 'bg-slate-800 border-slate-700 text-slate-400 hover:text-slate-200'
                                                        }`}
                                                >
                                                    {cat}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}

                            <div className="p-6 max-h-[60vh] overflow-y-auto">
                                {promptLibrary.length === 0 ? (
                                    <div className="text-center py-16 text-slate-500">
                                        <BookMarked className="w-16 h-16 mx-auto mb-4 opacity-20" />
                                        <h3 className="text-lg font-medium text-slate-400 mb-2">Your prompt library is empty</h3>
                                        <p className="text-sm max-w-md mx-auto">Type a prompt above and click "Save" to add it here. Give it a name and category so you can find it later.</p>
                                    </div>
                                ) : filtered.length === 0 ? (
                                    <div className="text-center py-16 text-slate-500">
                                        <Search className="w-12 h-12 mx-auto mb-3 opacity-20" />
                                        <p className="text-sm">No prompts match your search or filter.</p>
                                    </div>
                                ) : (
                                    <div className="space-y-3">
                                        {filtered.map(item => (
                                            <div
                                                key={item.id}
                                                className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 hover:border-indigo-500/50 transition-colors group"
                                            >
                                                <div className="flex items-start justify-between gap-3 mb-2">
                                                    <div className="flex-grow min-w-0">
                                                        {item.name && (
                                                            <h3 className="text-sm font-semibold text-white truncate">{item.name}</h3>
                                                        )}
                                                        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                                                            {item.category && (
                                                                <span className="text-[10px] px-2 py-0.5 rounded-full bg-indigo-500/10 text-indigo-300 border border-indigo-500/20">
                                                                    {item.category}
                                                                </span>
                                                            )}
                                                            <span className="text-[10px] text-slate-500 font-mono">
                                                                {new Date(item.timestamp).toLocaleDateString()}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <button
                                                        onClick={() => deletePromptLibraryItem(item.id)}
                                                        className="text-slate-600 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity p-1 flex-shrink-0"
                                                        title="Delete"
                                                        aria-label="Delete prompt"
                                                    >
                                                        <Trash2 size={14} />
                                                    </button>
                                                </div>
                                                <p className="text-xs text-slate-300 line-clamp-3 whitespace-pre-wrap mb-3 leading-relaxed">
                                                    {item.prompt}
                                                </p>
                                                <button
                                                    onClick={() => loadPromptFromLibrary(item)}
                                                    className="w-full bg-indigo-500/10 hover:bg-indigo-500/20 text-indigo-400 text-xs py-2 rounded border border-indigo-500/20 transition-colors font-medium"
                                                >
                                                    Load into prompt
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                );
            })()}

            {/* Full Screen Modal */}
            {showImageModal && modalImage && (
                <div
                    className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4 animate-fade-in"
                    onClick={() => setShowImageModal(false)}
                    role="dialog"
                    aria-modal="true"
                    aria-label="Enlarged image"
                >
                    <div className="relative max-w-[95vw] max-h-[95vh]">
                        <button
                            onClick={() => setShowImageModal(false)}
                            className="absolute -top-12 right-0 p-2 text-white/70 hover:text-white transition-colors"
                            aria-label="Close enlarged image"
                        >
                            <X size={32} />
                        </button>
                        <img
                            src={modalImage}
                            alt="Full Size"
                            className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl"
                            onClick={(e) => e.stopPropagation()}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProductImageGenPage;
